import React, { useContext, useMemo } from 'react';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { Box, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { FiChevronDown } from 'react-icons/fi';

import { OnboardingStepTitle } from '@vyce/core/src/components/OnboardingStepTitle';
import { AppCheckbox, AppTextField } from '@vyce/core/src/components/inputs';
import { AppChip } from '@vyce/core/src/components/AppChip';
import { DeviceContext } from '@vyce/core/src/contexts';

import { ManualClockContext } from './ManualClockProvider';

interface Option {
  id: string;
  name: string;
}

const name = 'bulkLocationIds';

const rules = {
  validate: (value?: string[]) => !!value?.length || 'Choose at least one location',
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tag: {
      display: 'none',
    },
    popupIndicator: {
      padding: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
    option: {
      gap: theme.spacing(1),
    },
  })
);

export const BulkLocationStep = () => {
  const classes = useStyles();
  const { bulkLocations } = useContext(ManualClockContext);
  const { isMobile } = useContext(DeviceContext);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const options = useMemo<Option[]>(
    () =>
      [...(bulkLocations ?? [])]
        .filter(location => !!location.uuid)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(location => ({ id: location.uuid as string, name: location.name })),
    [bulkLocations]
  );

  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <>
      <OnboardingStepTitle text="Which location?" />
      <Typography variant="body2" color="textSecondary">
        Everyone clocked in on these locations right now will be clocked out.
      </Typography>

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const selectedIds: string[] = field.value ?? [];
          const selectedOptions = options.filter(option => selectedIds.includes(option.id));

          const handleChange = (_: unknown, next: Option[]) => field.onChange(next.map(option => option.id));

          const removeOption = (id: string) => field.onChange(selectedIds.filter(item => item !== id));

          return (
            <>
              <Autocomplete<Option, true, false, false>
                multiple
                disableCloseOnSelect
                disablePortal={isMobile}
                id={name}
                classes={classes}
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                getOptionLabel={option => option.name}
                getOptionSelected={(option, value) => option.id === value.id}
                popupIcon={<FiChevronDown size="25px" />}
                renderOption={(option, { selected }) => (
                  <>
                    <AppCheckbox variant="rectangle" color="primary" checked={selected} />
                    {option.name}
                  </>
                )}
                renderInput={params => (
                  <AppTextField
                    {...params}
                    margin="normal"
                    label="Select locations"
                    autoComplete="off"
                    error={!!errorMessage}
                    helperText={errorMessage || ''}
                  />
                )}
              />

              <Box marginTop="12px">
                {selectedOptions.map(option => (
                  <AppChip
                    key={option.id}
                    style={{ margin: '0 6px 6px 0', maxWidth: '100%' }}
                    label={option.name}
                    onDelete={() => removeOption(option.id)}
                  />
                ))}
              </Box>
            </>
          );
        }}
      />
    </>
  );
};
